import { type ReactNode } from 'react';
import { motion, useReducedMotion } from 'motion/react';

/**
 * Entrance motion, used once per group rather than once per element.
 *
 * The entrance follows the glass design system: twenty pixels of travel with
 * an eight hundred millisecond ease-out. Individual metric cards use a shorter
 * stagger so the data stays easy to scan.
 *
 * Every component here collapses to no motion at all under
 * `prefers-reduced-motion`. Not a shorter animation, none: the setting exists
 * because motion makes some people ill, and a faster version of the thing that
 * makes them ill is still the thing.
 */

/** Shared easing. A gentle overshoot-free curve, the same one the CSS keyframes use. */
const EASE = [0.16, 1, 0.3, 1] as const;

export function Reveal({
  children,
  delay = 0,
  className,
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
}) {
  const still = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={still ? false : { opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: still ? 0 : 0.8, delay: still ? 0 : delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

/**
 * A grid whose children arrive one after another.
 *
 * The stagger is deliberately small. At 40ms a row of four tiles finishes in
 * 120ms, which reads as one movement with a direction rather than as four
 * things loading, and it never delays the number anyone came to read.
 */
export function RevealGroup({
  children,
  className,
  stagger = 0.04,
}: {
  children: ReactNode;
  className?: string;
  stagger?: number;
}) {
  const still = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial="hidden"
      animate="shown"
      variants={{
        hidden: {},
        shown: { transition: { staggerChildren: still ? 0 : stagger } },
      }}
    >
      {children}
    </motion.div>
  );
}

/** One child of a {@link RevealGroup}. Inherits the parent's stagger. */
export function RevealItem({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  const still = useReducedMotion();
  return (
    <motion.div
      className={className}
      variants={{
        hidden: still ? { opacity: 1 } : { opacity: 0, y: 10 },
        shown: { opacity: 1, y: 0, transition: { duration: still ? 0 : 0.3, ease: EASE } },
      }}
    >
      {children}
    </motion.div>
  );
}
